// MapLibre GL JS loader and basemap configuration.
//
// WHY A RUNTIME DYNAMIC IMPORT INSTEAD OF A NORMAL BUNDLED IMPORT
// MapLibre v6 ships ES modules plus a separate worker module that it locates
// at runtime from its own `import.meta.url`. Bundling it into index.js makes
// that URL the bundle's URL, and the worker chunk it then asks for was never
// emitted, so the map dies on first render. Verified against Bun 1.3 and
// maplibre-gl 6.5: `Bun.build()` bundles the library fine, the WORKER is what
// breaks. apps/web/build.ts therefore vendors MapLibre's four dist files into
// dist/vendor/maplibre and this module imports the entry from there, so
// `import.meta.url` inside it is the vendored file's real URL and the worker
// resolves to ./maplibre-gl-worker.mjs sitting right beside it.
//
// The specifier is built from location.origin at runtime on purpose: a
// literal string would be constant folded by the bundler, which would then
// try to resolve /vendor/... at build time and fail. Everything is served
// from our own origin, never a CDN, so this survives `default-src 'self'`.
import type * as maplibregl from "maplibre-gl";
import {
  MAP_ATTRIBUTION,
  MAP_SATELLITE_TILE_URL,
  MAP_STREET_STYLE_URL,
  MAP_TERRAIN_TILE_URL,
} from "./config";

export type MapLibreModule = typeof maplibregl;
export type MapInstance = maplibregl.Map;

export const VENDOR_PATH = "/vendor/maplibre/maplibre-gl.mjs";

let loading: Promise<MapLibreModule> | null = null;

export function loadMapLibre(): Promise<MapLibreModule> {
  if (!loading) {
    const origin = globalThis.location?.origin ?? "";
    const specifier = `${origin}${VENDOR_PATH}`;
    loading = import(specifier).then((module) => module as MapLibreModule);
  }
  return loading;
}

export type Basemap = "street" | "satellite";

/**
 * Satellite is a plain raster style assembled here rather than fetched, so
 * swapping MAP_SATELLITE_TILE_URL for a self hosted or Google backed tile
 * service needs no component change and no API key.
 */
export function styleFor(basemap: Basemap): string | maplibregl.StyleSpecification {
  if (basemap === "street") return MAP_STREET_STYLE_URL;
  return {
    version: 8,
    sources: {
      satellite: {
        type: "raster",
        tiles: [MAP_SATELLITE_TILE_URL],
        tileSize: 256,
        maxzoom: 19,
        attribution: MAP_ATTRIBUTION,
      },
    },
    layers: [{ id: "satellite", type: "raster", source: "satellite" }],
  } as unknown as maplibregl.StyleSpecification;
}

export const TERRAIN_SOURCE_ID = "traveldiary-terrain";

/** Terrarium encoded elevation tiles, keyless. Used only in the tilted view. */
export function terrainSourceSpec() {
  return {
    type: "raster-dem" as const,
    tiles: [MAP_TERRAIN_TILE_URL],
    tileSize: 256,
    maxzoom: 15,
    encoding: "terrarium" as const,
    attribution: MAP_ATTRIBUTION,
  };
}

export { MAP_ATTRIBUTION };
